import {
  LockOpenIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PhoneIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { TotalsRow } from 'components/TableTotals'
import { itemCountPerPage, LanguageOptions, NON_ENGLISH_LANGUAGE } from 'config'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  addCollectionCall,
  deleteCollectionCall,
  getCollectionCalls,
  getCollectionsQueue,
  releasePromiseToSweep,
  updateCollectionCall,
} from 'services'
import { Button, Input2, Pagination, Select } from 'stories/components'
import { confirm, formatDate, getLoanDisplayNo, getPrice2decimal, renderHeader } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import { AddCollectionCall } from './AddCollectionCall'
import { type CallPrefill, CallModal } from './CallModal'
import { NotesCell } from './NotesCell'
import { RecordingPlayer } from './RecordingPlayer'
import {
  type ICollectionCall,
  outcomeClass,
  outcomeLabels,
  phoneCallStatusLabels,
  ptpStatusClass,
  ptpStatusLabels,
} from './types'

const isEmpty = (v: any) => v === null || v === undefined || v === ''
const money = (v: any) => (isEmpty(v) ? '' : getPrice2decimal(v, false, true, true))

const duration = (seconds: any) => {
  const s = Math.max(0, Number(seconds) || 0)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/**
 * "Any non-English" first, because that is the list a bilingual desk works; the individual
 * languages below it are for a shop with a speaker of one in particular.
 */
const languageFilterOptions: Record<string, string> = {
  [NON_ENGLISH_LANGUAGE]: 'Any non-English',
  ...Object.fromEntries(LanguageOptions.map((language) => [language, language])),
}

const badge = (label: string, className: string) => (
  <span className={`px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap ${className}`}>{label}</span>
)

/**
 * The call log, per loan or across the book.
 *
 * Every row carries an outcome because the form will not save without one, which is what makes the
 * grid worth reading: a servicer scanning it sees what was tried and what came of it, not a column
 * of free text.
 */
export const CollectionCallsTable = ({ isLoan = true }: { isLoan?: boolean }) => {
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isCalling, setCalling] = useState(false)
  const [prefill, setPrefill] = useState<Record<string, any> | undefined>(undefined)
  const [data, setData] = useState<ICollectionCall[]>([])
  const [editingItem, setEditingItem] = useState<ICollectionCall | null>(null)
  const [query, setQuery] = useState('')
  const [loadedOnce, setLoadedOnce] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  // Promised amounts over the whole filtered log, summed server-side: the grid holds one page.
  const [totals, setTotals] = useState<{ ptpAmount: number } | null>(null)
  const [filters, setFilters] = useState<Record<string, any>>({
    orderBy: 'callDate',
    orderDir: '-1',
    isLoan: isLoan ? 'true' : 'false',
    pageNum: 0,
    count: itemCountPerPage,
    outcome: '',
    ptpStatus: '',
    language: '',
  })

  useEffect(() => {
    if (isLoan) setLoanNumber()
  }, [isLoan])

  useEffect(() => {
    refetch()
  }, [filters])

  useEffect(() => {
    if (!loadedOnce) return
    const timeOutId = setTimeout(() => !loading && refetch(), 700)
    return () => clearTimeout(timeOutId)
  }, [query])

  const refetch = (newFilters: Record<string, any> | null = null) => {
    setLoading(true)
    // The portfolio read goes to its own endpoint: the loan-scoped one requires a loan-number
    // header, which does not exist on a screen that was never opened from a loan.
    const fetcher = isLoan ? getCollectionCalls : getCollectionsQueue
    fetcher({ ...(newFilters || filters), query })
      .then((res: any) => {
        setLoadedOnce(true)
        setData(res?.results || [])
        setTotalCount(res?.totalCount || 0)
        setTotals(res?.totals || null)
      })
      .finally(() => setLoading(false))
  }

  const onAdd = () => {
    setEditingItem(null)
    setPrefill(undefined)
    setIsOpen(true)
  }

  /**
   * A call that has just been placed goes straight into the ordinary log form with what is already
   * known filled in. The collector supplies the outcome and hangs up, rather than dialing in one
   * place and remembering to write it up in another.
   */
  const onCallDone = (values: CallPrefill) => {
    setCalling(false)
    setEditingItem(null)
    setPrefill(values)
    setIsOpen(true)
  }

  const onEdit = (item: ICollectionCall) => {
    setEditingItem(item)
    setIsOpen(true)
  }

  const onSubmit = async (newData: Record<string, any>) => {
    setLoading(true)
    if (editingItem) await updateCollectionCall(editingItem.id, newData)
    else await addCollectionCall(newData)
    setIsOpen(false)
    setEditingItem(null)
    refetch()
  }

  const onDelete = async (item: ICollectionCall, index: number) => {
    const content = (
      <div className="mb-4 text-[18px]">
        Do you want to remove this call?
        <br />
        No. {index + 1}
      </div>
    )
    if (!(await confirm(content))) return
    setLoading(true)
    await deleteCollectionCall(item.id)
    refetch()
  }

  /**
   * A status a person set takes the promise out of the nightly check for good. That is the point of
   * it, but it has to be undoable from the row it applies to, and without editing the promised date
   * or amount to force it.
   */
  const onRelease = async (item: ICollectionCall) => {
    const content = (
      <div className="mb-4 text-[18px]">
        Hand this promise back to the nightly check?
        <br />
        <span className="text-[15px] text-gray-600">
          Its status was set by {item.ptpStatusSetBy}. It goes back to Open and is judged against the
          money received in the promise window.
        </span>
      </div>
    )
    if (!(await confirm(content))) return
    setLoading(true)
    await releasePromiseToSweep(item.id)
    refetch()
  }

  const onSort = (key: string, dir: number) => {
    const newFilters = cloneDeep(filters)
    newFilters.orderBy = key
    newFilters.orderDir = `${dir}`
    setFilters(newFilters)
  }

  const onFilterChange = (key: string, value: string) => {
    const newFilters = cloneDeep(filters)
    newFilters[key] = value
    // Back to page one: the page you were on rarely exists in the narrowed set.
    newFilters.pageNum = 0
    setFilters(newFilters)
  }

  const onPageNavigate = (num: number) => {
    const newFilters = cloneDeep(filters)
    newFilters.pageNum = num
    setFilters(newFilters)
  }

  const sortableHeaders = [
    { title: 'Loan', key: 'loanNumber' },
    { title: 'Call Date', key: 'callDate' },
    { title: 'Direction', key: 'direction' },
    { title: 'Spoke With', key: 'contactName' },
    { title: 'Outcome', key: 'outcome' },
    { title: 'PTP Date', key: 'ptpDate' },
    { title: 'PTP Amount', key: 'ptpAmount' },
    { title: 'PTP Status', key: 'ptpStatus' },
    { title: 'Employee', key: 'employee' },
    { title: 'Placed', key: 'durationSec' },
    { title: 'Notes', key: 'notes' },
  ].filter((h) => isLoan === false || h.key !== 'loanNumber')

  return (
    <div className="relative bg-white">
      {isLoan && <h2 className="text-2xl font-bold flex items-center mb-5">Collections Calls</h2>}
      <LayoutLoading show={loading} />
      <div className="flex flex-wrap justify-end items-end gap-3">
        <div className="w-56">
          <Select
            id="collection-call-outcome-filter"
            title="Outcome"
            options={outcomeLabels}
            hasDefaultOption
            defaultOptionText="All outcomes"
            value={filters.outcome}
            onChange={(value: any) => onFilterChange('outcome', value)}
          />
        </div>
        <div className="w-48">
          <Select
            id="collection-call-ptp-filter"
            title="Promise Status"
            options={ptpStatusLabels}
            hasDefaultOption
            defaultOptionText="Any"
            value={filters.ptpStatus}
            onChange={(value: any) => onFilterChange('ptpStatus', value)}
          />
        </div>
        {!isLoan && (
          <div className="w-52">
            <Select
              id="collection-call-language-filter"
              title="Language"
              options={languageFilterOptions}
              hasDefaultOption
              defaultOptionText="Any"
              value={filters.language}
              onChange={(value: any) => onFilterChange('language', value)}
            />
          </div>
        )}
        {!isLoan && (
          <div className="w-72">
            <Input2
              type="search"
              title="Search"
              hasIcon
              icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
              value={query}
              onChange={(value: any) => setQuery(value)}
            />
          </div>
        )}
        {isLoan && (
          <Button onClick={() => setCalling(true)} className="px-[24px]">
            <span className="flex items-center gap-1">
              <PhoneIcon className="w-4 h-4" />
              Call
            </span>
          </Button>
        )}
        {isLoan && (
          <Button onClick={onAdd} color="gray" className="px-[24px]">
            Log Call
          </Button>
        )}
      </div>

      {/* Height-capped so the horizontal scrollbar rides the bottom of the grid instead of the
          bottom of the page: a loan with a couple of hundred calls used to mean scrolling past
          every one of them to reach it, and then tracking back up to read the right-hand columns. */}
      <div className="parties-container overflow-auto max-h-[70vh] mb-6 mt-6 shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-6 py-3 sticky top-0 z-20 bg-slate-50 dark:bg-gray-700">
                No
              </th>
              {sortableHeaders.map(({ title, key }, index) =>
                renderHeader({
                  title,
                  sortable: true,
                  key,
                  sortOrder: filters.orderBy == key ? parseInt(filters.orderDir) : 0,
                  index,
                  onSort: (key: string, dir: number) => onSort(key, dir),
                  alignClassName: key === 'ptpAmount' ? 'justify-end' : '',
                  className: 'sticky top-0 z-20 bg-slate-50 dark:bg-gray-700',
                }),
              )}
              {/* Pinned right so the edit action travels with the row, and frozen top with the rest
                  of the header. The corner cell is stuck both ways at once, so it outranks both. */}
              <th scope="col" className="px-6 py-3 sticky right-0 top-0 z-30 bg-slate-50 dark:bg-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="text-[14.5px] text-gray-900">
            {data.map((item, index) => (
              <tr key={item.id} className={`border-b ${index % 2 ? 'bg-gray-50' : ''}`}>
                <td className="px-6 py-3">{index + 1 + itemCountPerPage * (filters.pageNum || 0)}</td>
                {!isLoan && (
                  <td className="px-2 py-3 whitespace-nowrap">
                    <Link
                      to={`/servicing/${item.loanNumber}`}
                      className="text-shade-blue hover:underline font-semibold"
                    >
                      {getLoanDisplayNo(item)}
                    </Link>
                    {item.borrowerName ? <p className="text-[11px] text-gray-500">{item.borrowerName}</p> : null}
                    {item.preferredLanguage && item.preferredLanguage !== 'English' ? (
                      <p className="mt-0.5">{badge(item.preferredLanguage, 'bg-amber-100 text-amber-700')}</p>
                    ) : null}
                    {item.daysPastDue ? (
                      <p className="text-[11px] text-red-600">{item.daysPastDue} days past due</p>
                    ) : null}
                  </td>
                )}
                <td className="px-2 py-3 whitespace-nowrap">
                  {formatDate(item.callDate)}
                  {item.callTime ? <span className="text-[11px] text-gray-500 ml-1">{item.callTime}</span> : null}
                </td>
                <td className="px-2 py-3 capitalize">{item.direction}</td>
                <td className="px-2 py-3">
                  {item.contactName}
                  {item.contactPhone ? <p className="text-[11px] text-gray-500">{item.contactPhone}</p> : null}
                </td>
                <td className="px-2 py-3">
                  {badge(
                    outcomeLabels[item.outcome] || item.outcome,
                    outcomeClass[item.outcome] || 'bg-gray-100 text-gray-700',
                  )}
                </td>
                <td className="px-2 py-3 whitespace-nowrap">{formatDate(item.ptpDate)}</td>
                <td className="px-2 py-3 text-right tabular-nums">{money(item.ptpAmount)}</td>
                <td className="px-2 py-3">
                  {item.ptpStatus
                    ? badge(ptpStatusLabels[item.ptpStatus] || item.ptpStatus, ptpStatusClass[item.ptpStatus] || '')
                    : ''}
                  {/* What actually arrived in the promise window, once the sweep has settled it. */}
                  {item.ptpResolvedDate ? (
                    <p className="text-[11px] text-gray-500 whitespace-nowrap">
                      {formatDate(item.ptpResolvedDate)}
                      {isEmpty(item.ptpPaidAmount) ? '' : ` · ${money(item.ptpPaidAmount)}`}
                    </p>
                  ) : null}
                  {/* Nothing else on the screen says the sweep has stopped looking at this row. */}
                  {item.ptpStatusSetBy ? (
                    <p
                      className="text-[11px] text-amber-700 whitespace-nowrap"
                      title={`Set by ${item.ptpStatusSetBy}. The nightly check will not change it.`}
                    >
                      Set by hand
                    </p>
                  ) : null}
                </td>
                <td className="px-2 py-3 whitespace-nowrap">{item.employee}</td>
                {/* Only a call the platform dialed has anything to show here. One typed in from a
                    paper log is still a perfectly good record; it simply has no carrier behind it. */}
                <td className="px-2 py-3 whitespace-nowrap">
                  {item.phoneCallId ? (
                    <>
                      <span className="text-[12px] text-gray-600">
                        {item.durationSec
                          ? duration(item.durationSec)
                          : phoneCallStatusLabels[item.phoneCallStatus || ''] || ''}
                      </span>
                      {item.hasRecording ? <RecordingPlayer phoneCallId={item.phoneCallId} /> : null}
                    </>
                  ) : null}
                </td>
                <td className="px-2 py-3 align-top">
                  <NotesCell text={item.notes} />
                </td>
                <td className={`px-5 py-3 sticky right-0 z-10 ${index % 2 ? 'bg-gray-50' : 'bg-white'}`}>
                  <div className="flex gap-1">
                    <span
                      className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                      onClick={() => onEdit(item)}
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </span>
                    {item.ptpStatusSetBy ? (
                      <span
                        className="text-amber-700 cursor-pointer hover-shadow1 p-1 rounded"
                        onClick={() => onRelease(item)}
                        title="Hand this promise back to the nightly check"
                      >
                        <LockOpenIcon className="w-4 h-4" />
                      </span>
                    ) : null}
                    <span
                      className="text-red-800 cursor-pointer hover-shadow1 p-1 rounded"
                      onClick={() => onDelete(item, index)}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </span>
                  </div>
                </td>
              </tr>
            ))}
            {!data.length && !loading && (
              <tr>
                <td className="px-6 py-4 text-gray-500" colSpan={sortableHeaders.length + 2}>
                  No calls logged.
                </td>
              </tr>
            )}
          </tbody>
          {data.length > 0 && totals && (
            <TotalsRow
              sticky
              tdClass="px-2 py-3"
              cells={[
                { value: 'Totals', className: 'px-6 py-3' },
                ...(isLoan === false ? [''] : []),
                '',
                '',
                '',
                '',
                '',
                { value: totals.ptpAmount, className: 'px-2 py-3' },
                '',
                '',
                '',
                '',
                { value: '', className: 'px-6 py-3' },
              ]}
            />
          )}
        </table>
      </div>

      {!isLoan && (
        <div className="flex justify-end items-center mb-6">
          <Pagination
            totalCount={totalCount}
            itemCountPerPage={itemCountPerPage}
            onNavigate={onPageNavigate}
            pageNum={filters.pageNum}
          />
        </div>
      )}

      {isOpen && (
        <AddCollectionCall item={editingItem} prefill={prefill} onClose={() => setIsOpen(false)} onUpdate={onSubmit} />
      )}
      {isCalling && (
        <CallModal loanNumber={Number(setLoanNumber())} onClose={() => setCalling(false)} onDone={onCallDone} />
      )}
    </div>
  )
}
